import {FC} from 'react'
import {Plus, Trash2, GripVertical} from 'lucide-react'

export type AdditionalField = {
  name: string
  type: string
  required: boolean
  options?: string
}

const FIELD_TYPES = [
  {label: 'Short Text', value: 'text'},
  {label: 'Long Text', value: 'textarea'},
  {label: 'Number', value: 'number'},
  {label: 'Yes / No', value: 'switch'},
  {label: 'Date', value: 'date'},
  {label: 'Time', value: 'time'},
  {label: 'Dropdown', value: 'select'},
]

type Props = {
  value: AdditionalField[]
  onChange: (fields: AdditionalField[]) => void
}

const AdditionalFieldsEditor: FC<Props> = ({value = [], onChange}) => {
  const fields = Array.isArray(value) ? value : []

  const addField = () => {
    onChange([...fields, {name: '', type: 'text', required: false}])
  }

  const removeField = (idx: number) => {
    onChange(fields.filter((_, i) => i !== idx))
  }

  const updateField = (idx: number, key: keyof AdditionalField, val: any) => {
    const updated = fields.map((f, i) => (i === idx ? {...f, [key]: val} : f))
    onChange(updated)
  }

  return (
    <div className='space-y-3'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <p className='text-xs text-gray-500 font-medium'>
          {fields.length === 0
            ? 'No additional questions added yet.'
            : `${fields.length} question${fields.length > 1 ? 's' : ''} added`}
        </p>
      </div>

      {/* Fields */}
      <div className='space-y-3'>
        {fields.map((field, idx) => (
          <div
            key={idx}
            className='group relative bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow'
          >
            {/* Question number badge */}
            <div className='absolute -top-2 -left-2 w-6 h-6 bg-[#008080] text-white text-xs font-bold rounded-full flex items-center justify-center shadow'>
              {idx + 1}
            </div>

            <div className='flex items-start gap-3'>
              {/* Grip Handle */}
              <div className='pt-2 text-gray-300'>
                <GripVertical className='w-4 h-4' />
              </div>

              {/* Main Content */}
              <div className='flex-1 space-y-3'>
                {/* Row 1: Question Name */}
                <input
                  type='text'
                  placeholder='Enter your question (e.g. "Years of experience")'
                  value={field.name}
                  onChange={(e) => updateField(idx, 'name', e.target.value)}
                  className='w-full text-sm py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080] transition-all placeholder:text-gray-400'
                />

                {/* Row 2: Type + Required + Options */}
                <div className='flex flex-wrap items-center gap-3'>
                  {/* Type Selector */}
                  <select
                    value={field.type}
                    onChange={(e) => updateField(idx, 'type', e.target.value)}
                    className='text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080] transition-all cursor-pointer min-w-[140px]'
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>

                  {/* Required Toggle */}
                  <label className='flex items-center gap-2 cursor-pointer select-none'>
                    <div className='relative'>
                      <input
                        type='checkbox'
                        checked={field.required}
                        onChange={(e) => updateField(idx, 'required', e.target.checked)}
                        className='sr-only peer'
                      />
                      <div className='w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-[#008080] transition-colors'></div>
                      <div className='absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform'></div>
                    </div>
                    <span className='text-xs font-medium text-gray-600'>Required</span>
                  </label>
                </div>

                {/* Row 3: Options (only for select type) */}
                {field.type === 'select' && (
                  <input
                    type='text'
                    placeholder='Comma-separated options (e.g. "Option A, Option B, Option C")'
                    value={field.options || ''}
                    onChange={(e) => updateField(idx, 'options', e.target.value)}
                    className='w-full text-sm py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-400 transition-all placeholder:text-amber-400'
                  />
                )}
              </div>

              {/* Delete Button */}
              <button
                type='button'
                onClick={() => removeField(idx)}
                className='p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100'
                title='Remove question'
              >
                <Trash2 className='w-4 h-4' />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Button */}
      <button
        type='button'
        onClick={addField}
        className='w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:border-[#008080] hover:text-[#008080] hover:bg-[#008080]/5 transition-all'
      >
        <Plus className='w-4 h-4' />
        Add Question
      </button>
    </div>
  )
}

export default AdditionalFieldsEditor
